import { Spacing } from '@persona-id/design-tokens';
import { EditIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { VFC } from 'react';
import styled from 'styled-components';

import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import RulesSection from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/RulesSection';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { UsableObjectConfig } from 'dashboard/lib/workflows';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowConditional } from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import { JsonSchema } from 'lib/json-schema';
import { TokensToNode } from 'lib/workflows';

const FIRST_ROUTE_HELP_TEXT = 'This route is always evaluated.';
const OTHER_ROUTE_HELP_TEXT = 'Only evaluated if none of the prior conditions are met.';

const HeadingContainer = styled.div<{
  fullOpacity: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  opacity: ${({ fullOpacity }) => (fullOpacity ? '1' : '0.4')};
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[3]};
`;

const RouteHeading = styled(Heading)`
  padding-right: ${Spacing[1]};
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 0;
`;

const RouteName = styled(Label).attrs(() => ({ size: 'lg', weight: 'bold' }))`
  display: -webkit-box;
  padding-right: ${Spacing[1]};
  overflow: hidden;
  white-space: normal;
  word-wrap: pre-wrap;
  text-overflow: ellipsis;
  margin-right: 0;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

interface ConditionalConfigurationProps {
  conditional: WorkflowConditional;
  deleteEnabled?: boolean;
  editable: boolean;
  hideContext?: boolean;
  errors: WorkflowError[];
  jsonSchema: JsonSchema;
  /**
   * selectively enables conditional editing,
   * prevents route deletion for Inquiries basic
   */
  isLimited: boolean;
  routeIndex: number;
  tokensToDisplayValue: Record<string, string>;
  tokensToSteps: TokensToNode;
  usableObjects: UsableObjectConfig[];
  deleteRoute: () => void;
  setConditionalRouteToEdit: (routeNumber: number) => void;
  inInquiryInspectRunPath?: boolean;
}

const ConditionalConfiguration: VFC<ConditionalConfigurationProps> = ({
  conditional,
  deleteEnabled,
  editable,
  hideContext,
  errors,
  jsonSchema,
  isLimited,
  routeIndex,
  tokensToDisplayValue,
  tokensToSteps,
  usableObjects,
  deleteRoute,
  setConditionalRouteToEdit,
  inInquiryInspectRunPath,
}) => {
  const { name, rules } = conditional;
  const { renderErrorMessageFor } = useWorkflowError(errors);

  const routeNumber = routeIndex + 1;
  const openEditPanel = () => {
    setConditionalRouteToEdit(routeNumber);
  };

  return (
    <>
      <HeadingContainer fullOpacity={true}>
        <div>
          {!hideContext && (
            <RouteHeading size='sm'>
              Route {routeNumber}
              {name !== '' && ':'}
            </RouteHeading>
          )}
          {name !== '' && <RouteName>{name}</RouteName>}
        </div>
        {(editable || isLimited) && (
          <HStack>
            <HorizontalContainer>
              {isLimited && (
                <ProductUpsellTooltip
                  side='top'
                  accessTier={EditorAccessTier.InquiriesBasic}
                  learnMoreId='edit-conditional-route-config'
                  trigger={
                    <div>
                      <InquiriesGatingSticker
                        dataMarketingIdSuffix='edit-conditional-route-config'
                        stickerType={EnhancedGatingStickerType.Limited}
                      />
                    </div>
                  }
                />
              )}
              <Button
                type='button'
                variant='basic'
                width='icon'
                aria-label={`Edit conditional route ${routeNumber}`}
                onClick={openEditPanel}
                disabled={!editable}
                data-test={`conditional-configuration__open-edit-panel-${routeIndex}`}
                id={`conditional-configuration__open-edit-panel-${routeIndex}`}
              >
                <EditIcon />
              </Button>
              {deleteEnabled && (
                <Button
                  type='button'
                  variant='danger'
                  width='icon'
                  onClick={deleteRoute}
                  disabled={!editable}
                  data-test={`conditional-configuration__delete-route-${routeIndex}`}
                  id={`conditional-configuration__delete-route-${routeIndex}`}
                >
                  <TrashIcon />
                </Button>
              )}
            </HorizontalContainer>
          </HStack>
        )}
      </HeadingContainer>
      <Spacer height={1} />
      {!hideContext && (
        <Paragraph size='sm'>
          {routeIndex === 0 ? FIRST_ROUTE_HELP_TEXT : OTHER_ROUTE_HELP_TEXT}
        </Paragraph>
      )}
      <Spacer height={1} />
      <RulesSection
        rules={rules}
        errors={errors}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        usableObjects={usableObjects}
        tokensToSteps={tokensToSteps}
        routeIndex={routeIndex}
        rootPathForField={`conditionals[${routeIndex}].`}
        inInquiryInspectRunPath={inInquiryInspectRunPath}
      />
      {renderErrorMessageFor([`conditionals[${routeIndex}].rules.or`, `branches[${routeIndex}]`])}
    </>
  );
};

export default ConditionalConfiguration;
